import { BusinessResponses } from '@/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Languages, Coins, Clock } from 'lucide-react'
import { useState, useEffect } from 'react'
import { BusinessModel } from '@/http/models/business/Business'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { TextInput } from '@/components/atoms/inputs'
import { Spinner } from '@/components/ui/spinner'

interface LocalizationSettingsProps {
    businessProfile: any | null
    onRefresh: () => Promise<void>
}

export const LocalizationSettings = ({ businessProfile, onRefresh }: LocalizationSettingsProps) => {
    const [isSaving, setIsSaving] = useState(false)

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            currency: 'usd',
            lang: 'en',
            timezone: 'utc',
        },
    })

    useEffect(() => {
        if (businessProfile?.app_settings) {
            reset({
                currency: businessProfile.app_settings.currency || 'usd',
                lang: businessProfile.app_settings.lang || 'en',
                timezone: businessProfile.app_settings.timezone || 'utc',
            })
        }
    }, [businessProfile, reset])

    const onSubmit = async (data: any) => {
        setIsSaving(true)
        try {
            const response = await BusinessModel.updateBusinessProfile({
                body: {
                    app_settings: {
                        ...businessProfile?.app_settings,
                        ...data,
                    },
                },
            })
            if (response.success) {
                toast.success('Localization settings updated')
                await onRefresh()
            } else {
                toast.error('Failed to update settings')
            }
        } catch (error) {
            toast.error('An error occurred')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Store defaults</CardTitle>
                    <CardDescription>The currency, language and time zone your store uses.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <TextInput
                            control={control}
                            name="currency"
                            label="Currency code"
                            placeholder="USD"
                            icon={<Coins className="size-4" />}
                            errors={errors}
                            upperCase
                        />
                        <TextInput
                            control={control}
                            name="lang"
                            label="Language code"
                            placeholder="en"
                            icon={<Languages className="size-4" />}
                            errors={errors}
                        />
                        <TextInput
                            control={control}
                            name="timezone"
                            label="Time zone"
                            placeholder="UTC"
                            icon={<Clock className="size-4" />}
                            errors={errors}
                        />
                    </div>
                </CardContent>
            </Card>

            <div className="flex justify-end">
                <Button onClick={handleSubmit(onSubmit)} disabled={isSaving}>
                    {isSaving && <Spinner />}
                    Save
                </Button>
            </div>
        </div>
    )
}
